import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, Loader2, Trash2, ChevronDown, ChevronUp, UserPlus, FileText, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { AIWorkoutGeneratorModal } from './AIWorkoutGeneratorModal';
import { ProgramEditorForm } from './ProgramEditorForm';
import { isSessionExpired } from '../../utils/adminSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Brouillons de programmes générés par IA sans client assigné — le coach génère à l'avance
 * puis assigne à un client plus tard (ou supprime). Vue globale, accessible avant de sélectionner un client. */
export const AIWorkoutDraftsPanel = ({ csrfToken, clients }) => {
  const [drafts, setDrafts] = useState(null);
  const [showAIModal, setShowAIModal] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [assignEmail, setAssignEmail] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null); // { draft_id, name, goal, weeks_target, tonnage_growth_target_pct, sessions }
  const [setTypes, setSetTypes] = useState([]);
  const [library, setLibrary] = useState({ muscle_groups: [], exercises: [] });
  const [saveBusy, setSaveBusy] = useState(false);

  const fetchWithCsrf = (url, options = {}) => fetch(url, {
    ...options, credentials: 'include',
    headers: { ...options.headers, 'X-CSRF-Token': csrfToken },
  });

  const load = useCallback(() => {
    fetch(`${BACKEND_URL}/api/admin/programs-pro/drafts`, { credentials: 'include' })
      .then((r) => (isSessionExpired(r) ? Promise.reject(null) : r.json()))
      .then((d) => setDrafts(d.drafts || [])).catch(() => setDrafts([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/programs-pro/set-types`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => setSetTypes(d.set_types || [])).catch(() => {});
    fetch(`${BACKEND_URL}/api/admin/programs-pro/exercise-library`, { credentials: 'include' })
      .then((r) => r.json()).then(setLibrary).catch(() => {});
  }, []);

  const upd = (mut) => setEditing((e) => { const c = JSON.parse(JSON.stringify(e)); mut(c); return c; });

  const saveEditing = () => {
    if (!editing.name.trim()) { toast.error('Le nom du programme est requis.'); return; }
    if (!editing.goal.trim()) { toast.error("L'objectif est requis."); return; }
    setSaveBusy(true);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/programs-pro/drafts/${editing.draft_id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(editing),
    })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then(() => { setEditing(null); load(); toast.success('Brouillon mis à jour !'); })
      .catch((r) => { if (r) toast.error("Échec de l'enregistrement du brouillon."); })
      .finally(() => setSaveBusy(false));
  };

  const onGenerated = (program) => {
    setShowAIModal(false);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/programs-pro/drafts`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(program),
    })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then(() => { load(); toast.success('Brouillon IA enregistré ! Assignez-le à un client quand vous voulez.'); })
      .catch((r) => { if (r) toast.error("Échec de l'enregistrement du brouillon."); });
  };

  const assign = (draftId) => {
    const email = assignEmail[draftId];
    if (!email) { toast.error('Choisissez un client.'); return; }
    setBusyId(draftId);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/programs-pro/drafts/${draftId}/assign`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }),
    })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : r.json().then((j) => Promise.reject(j));
      })
      .then(() => { toast.success('Programme assigné et client notifié !'); load(); })
      .catch((j) => { if (j) toast.error(j?.detail || "Échec de l'assignation."); })
      .finally(() => setBusyId(null));
  };

  const del = (draftId) => {
    if (!window.confirm('Supprimer définitivement ce brouillon ?')) return;
    setBusyId(draftId);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/programs-pro/drafts/${draftId}`, { method: 'DELETE' })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then(() => { load(); toast.success('Brouillon supprimé.'); })
      .catch((r) => { if (r) toast.error('Échec de la suppression.'); })
      .finally(() => setBusyId(null));
  };

  return (
    <div className="rounded-2xl bg-zinc-900/60 border border-violet-500/20 p-5 mb-5" data-testid="ai-drafts-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-violet-400" /> Brouillons IA
          {drafts && drafts.length > 0 && <span className="text-xs font-normal text-zinc-500">({drafts.length})</span>}
        </h3>
        <button onClick={() => setShowAIModal(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white text-sm font-bold rounded-xl hover:scale-105 transition-transform"
          data-testid="ai-new-draft-btn">
          <Sparkles className="w-4 h-4" /> Générer un brouillon
        </button>
      </div>

      {showAIModal && (
        <AIWorkoutGeneratorModal csrfToken={csrfToken} onClose={() => setShowAIModal(false)} onGenerated={onGenerated} />
      )}

      {drafts === null ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-violet-400 animate-spin" /></div>
      ) : editing ? (
        <ProgramEditorForm
          editing={editing} upd={upd} library={library} setTypes={setTypes} csrfToken={csrfToken}
          onSave={saveEditing} onCancel={() => setEditing(null)} busy={saveBusy} testIdPrefix="draft-"
        />
      ) : drafts.length === 0 ? (
        <p className="text-sm text-zinc-500 py-4 text-center">Aucun brouillon pour l'instant. Générez-en un sans avoir besoin de choisir un client tout de suite.</p>
      ) : (
        <div className="space-y-2">
          {drafts.map((d) => (
            <div key={d.draft_id} className="bg-zinc-800/60 border border-white/10 rounded-xl p-3" data-testid={`ai-draft-card-${d.draft_id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-white font-semibold">{d.name}</p>
                  <p className="text-xs text-zinc-500 line-clamp-2">{d.goal} · {d.sessions.length} séance(s) · {d.weeks_target} sem.</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setExpanded(expanded === d.draft_id ? null : d.draft_id)} className="p-2 text-zinc-400 hover:text-white transition-colors" data-testid={`ai-draft-toggle-${d.draft_id}`}>
                    {expanded === d.draft_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  <button onClick={() => setEditing(JSON.parse(JSON.stringify(d)))} className="p-2 text-zinc-400 hover:text-primary transition-colors" title="Modifier ce brouillon" data-testid={`ai-draft-edit-${d.draft_id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => del(d.draft_id)} disabled={busyId === d.draft_id} className="p-2 text-zinc-400 hover:text-red-400 transition-colors disabled:opacity-50" data-testid={`ai-draft-delete-${d.draft_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {expanded === d.draft_id && (
                <div className="mt-3 pt-3 border-t border-white/10 space-y-3" data-testid={`ai-draft-detail-${d.draft_id}`}>
                  {d.sessions.map((s, i) => (
                    <div key={i}>
                      <p className="text-xs font-bold text-violet-300 mb-1">{s.session_label}</p>
                      <ul className="space-y-1">
                        {s.exercises.map((ex) => (
                          <li key={ex.exercise_id} className="text-xs text-zinc-400 flex items-center gap-2 flex-wrap">
                            {ex.image_url ? (
                              <img src={`${BACKEND_URL}${ex.image_url}`} alt={ex.name} loading="lazy" className="w-9 h-9 rounded object-cover border border-white/10 flex-shrink-0" />
                            ) : (
                              <FileText className="w-3 h-3 text-zinc-600 flex-shrink-0" />
                            )}
                            <span className="text-zinc-300">{ex.name}</span>
                            <span className="text-zinc-600">· {ex.sets_count}×{ex.reps_scheme} · {ex.rest_seconds}s{ex.tempo ? ` · tempo ${ex.tempo}` : ''}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}

                  <div className="flex items-center gap-2 pt-2">
                    <select value={assignEmail[d.draft_id] || ''} onChange={(e) => setAssignEmail((a) => ({ ...a, [d.draft_id]: e.target.value }))}
                      className="flex-1 bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid={`ai-draft-assign-select-${d.draft_id}`}>
                      <option value="">Choisir un client...</option>
                      {(clients || []).map((c) => <option key={c.email} value={c.email}>{c.name || c.email}</option>)}
                    </select>
                    <button onClick={() => assign(d.draft_id)} disabled={busyId === d.draft_id}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-primary to-pink-500 text-white text-sm font-bold rounded-lg hover:scale-105 transition-transform disabled:opacity-50"
                      data-testid={`ai-draft-assign-btn-${d.draft_id}`}>
                      {busyId === d.draft_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Assigner
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AIWorkoutDraftsPanel;
